import { loadSupabaseConfig } from './modules/config-loader.js';
import { initSupabase } from './modules/supabase-client.js';
import { resolvePublicPath } from './modules/path-utils.js';

let SUPABASE_URL = '';
let SUPABASE_ANON_KEY = '';
let SUPABASE_TABLES = {};
let hasValidConfig = false;
let supabase = null;
const labToolsContainer = document.querySelector('[data-lab-tools]');

const targets = {
  exhibits: document.querySelector('[data-exhibit-list]'),
  encyclopedia: document.querySelector('[data-encyclopedia-grid]'),
  collections: document.querySelector('[data-atelier-list]'),
  tools: labToolsContainer,
  timeline: document.querySelector('[data-timeline]'),
  newsletter: document.querySelector('[data-newsletter-list]'),
  featured: document.querySelector('[data-featured-articles]')
};

const subscribeElements = {
  modal: document.querySelector('[data-subscribe-modal]'),
  open: document.querySelector('[data-open-subscribe]'),
  close: document.querySelector('[data-close-subscribe]'),
  form: document.querySelector('[data-subscribe-form]'),
  status: document.querySelector('[data-subscribe-status]')
};

function applySupabaseConfig(config = {}) {
  SUPABASE_URL = config.SUPABASE_URL || '';
  SUPABASE_ANON_KEY = config.SUPABASE_ANON_KEY || '';
  SUPABASE_TABLES = config.SUPABASE_TABLES || {};

  hasValidConfig =
    Boolean(SUPABASE_URL) &&
    Boolean(SUPABASE_ANON_KEY) &&
    !SUPABASE_URL.includes('your-project') &&
    SUPABASE_ANON_KEY !== 'public-anon-key';

  supabase = hasValidConfig ? initSupabase(SUPABASE_URL, SUPABASE_ANON_KEY) : null;
}

function toggleSubscribeModal(show) {
  if (!subscribeElements.modal) return;
  subscribeElements.modal.hidden = !show;
  document.body.style.overflow = show ? 'hidden' : '';
}

function setSubscribeStatus(message, isError = false) {
  if (!subscribeElements.status) return;
  subscribeElements.status.textContent = message;
  subscribeElements.status.classList.toggle('error', isError);
}

function subscribeCanSubmit() {
  return hasValidConfig && Boolean(supabase);
}

function getSubscriberTableName() {
  return SUPABASE_TABLES.subscribers || 'newsletter_subscribers';
}

function buildSubscriberPayload(fullName, email) {
  const payload = {
    full_name: fullName,
    email
  };

  const subscriberTable = getSubscriberTableName();
  const newsletterTable = SUPABASE_TABLES.newsletter;
  const usesNewsletterSchema = newsletterTable && subscriberTable === newsletterTable;

  if (usesNewsletterSchema) {
    const fallbackTitle = fullName ? `${fullName} (${email})` : email;
    payload.title = fallbackTitle;
    payload.published_at = new Date().toISOString();
    payload.position = Math.floor(Date.now() / 1000);
  }

  return payload;
}

function bindSubscribeHandlers() {
  if (!subscribeElements.open || !subscribeElements.modal || !subscribeElements.form) return;

  subscribeElements.open.addEventListener('click', () => {
    toggleSubscribeModal(true);
    setSubscribeStatus(
      subscribeCanSubmit() ? 'We send gentle, infrequent updates.' : 'Subscribe form is in preview mode.'
    );
  });

  subscribeElements.close?.addEventListener('click', () => toggleSubscribeModal(false));
  subscribeElements.modal.addEventListener('click', (event) => {
    if (event.target === subscribeElements.modal) toggleSubscribeModal(false);
  });

  subscribeElements.form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const formData = new FormData(subscribeElements.form);
    const fullName = formData.get('name')?.toString().trim() || null;
    const email = formData.get('email')?.toString().trim();
    const canSubmit = subscribeCanSubmit();

    if (!email) {
      setSubscribeStatus('Email is required.', true);
      return;
    }

    if (!canSubmit) {
      setSubscribeStatus('Subscription service is not configured yet.', true);
      return;
    }

    try {
      setSubscribeStatus('Saving your subscription…');
      const tableName = getSubscriberTableName();
      const payload = buildSubscriberPayload(fullName, email);
      const { error } = await supabase.from(tableName).insert(payload);
      if (error) throw new Error(error.message);
      setSubscribeStatus('You are on the list!');
      subscribeElements.form.reset();
      toggleSubscribeModal(false);
    } catch (error) {
      console.error(error);
      setSubscribeStatus(error.message || 'Subscription failed, please try again.', true);
    }
  });
}

const statusCopy = {
  live: { label: 'Live Tool', className: 'tag' },
  upcoming: { label: 'Coming Soon', className: 'tag muted' },
  draft: { label: 'In Progress', className: 'tag muted' }
};

const DEFAULT_TIMELINE = [
  {
    year_label: 'Sep 2023 — Birth',
    description: 'Croissant is born with golden Dutch markings.',
    year_index: 202309
  },
  {
    year_label: 'Dec 2023 — Family Arrival',
    description: 'He officially joins the Croissant Bunny family.',
    year_index: 202312
  },
  {
    year_label: '2024 — IP & Stickers',
    description: 'Croissant debuts personalized IP visuals and sticker sets.',
    year_index: 202401
  },
  {
    year_label: '2025 — Website Launch',
    description: 'The Croissant Bunny site opens as an evolving archive.',
    year_index: 202501
  }
];

const DEFAULT_FEATURED_ARTICLES = [
  {
    slug: 'rabbit-line-study-warmups',
    title: 'Rabbit Line Study Warmups',
    summary: '15-minute morning drills with Croissant: silhouettes, corrections, and calm lines.',
    image_url: 'assets/images/articles/rabbit-line-study-warmups-1.png'
  },
  {
    slug: 'cartoon-rabbit-outlines',
    title: 'Cartoon Rabbit Outlines',
    summary: 'Rounder proportions and clean outlines that stay traceable for playful poses.',
    image_url: 'assets/images/articles/cartoon-rabbit-outlines-1.png'
  },
  {
    slug: 'trace-ready-rabbit-poses',
    title: 'Trace-ready Rabbit Poses',
    summary: 'Six daily shapes I observe from Croissant, converted into ready-to-trace linework.',
    image_url: 'assets/images/articles/trace-ready-rabbit-poses-1.png'
  },
  {
    slug: 'rabbit-expression-guide',
    title: 'Rabbit Expression Guide',
    summary: 'Ears, eyes, and whiskers: first-person notes on capturing calm, curious, alert moods.',
    image_url: 'assets/images/articles/rabbit-expression-guide-1.png'
  },
  {
    slug: 'common-rabbit-drawing-mistakes',
    title: 'Common Rabbit Drawing Mistakes',
    summary: 'Seven errors I still make—plus the quick fixes that keep outlines clean.',
    image_url: 'assets/images/articles/common-rabbit-drawing-mistakes-1.png'
  },
  {
    slug: 'care-setup-for-sketching',
    title: 'Care Setup for Sketching',
    summary: 'How I set light, backdrops, and breaks so Croissant models comfortably.',
    image_url: 'assets/images/articles/care-setup-for-sketching-1.png'
  }
];

const DEFAULT_LAB_TOOLS = [
  {
    title: 'Rabbit Retouching Lab',
    description: 'Upload a candid rabbit photo, pick an ambiance, and let the atelier retouch lighting and color palettes.',
    status: 'live spotlight',
    cta_label: 'Try',
    cta_url: 'tools/rabbit-retouching.html',
    emphasis: true
  },
  {
    title: 'How to Draw a Rabbit',
    description: 'Generate step-by-step rabbit drawing tutorials in line or cartoon styles with printable practice sheets.',
    status: 'live spotlight',
    cta_label: 'Try',
    cta_url: 'tools/how-to-draw-a-rabbit.html',
    emphasis: true
  },
  {
    title: 'Rabbit Wallpaper Studio',
    description: 'Design device-ready wallpapers starring Croissant Bunny palettes, motifs, and Dutch-marked rabbits.',
    status: 'live spotlight',
    cta_label: 'Try',
    cta_url: 'tools/rabbit-wallpaper-studio.html',
    emphasis: true
  },
  {
    title: 'Bunny Avatar Studio',
    description: 'Compose a Croissant-style cartoon headshot by mixing breed archetypes with curated moods.',
    status: 'live spotlight',
    cta_label: 'Try',
    cta_url: 'tools/rabbit-avatar-studio.html',
    emphasis: true
  },
  {
    title: 'Rabbit Sketch Studio',
    description: 'Upload a photo and transform it into Croissant-style watercolor, pencil, or gouache sketches.',
    status: 'live spotlight',
    cta_label: 'Try',
    cta_url: 'tools/rabbit-sketch-studio.html',
    emphasis: true
  },
  {
    title: 'Rabbit Portrait Atelier',
    description: 'Generate formal portraits with curated backgrounds and bespoke styling cues.',
    status: 'live',
    cta_label: 'Try',
    cta_url: 'tools/rabbit-portrait-studio.html'
  },
  {
    title: 'Rabbit Daylife Grid',
    description: 'Generate a four-panel collage showing sunrise, noon, afternoon, and night scenes for Croissant-style rabbits.',
    status: 'live',
    cta_label: 'Try',
    cta_url: 'tools/rabbit-daylife-grid.html'
  },
  {
    title: 'Rabbit Name Card Generator',
    description: 'Transform your bunny’s name into a printable card with curated palettes, moods, and motifs.',
    status: 'live',
    cta_label: 'Try',
    cta_url: 'tools/rabbit-name-card.html'
  },
  {
    title: 'Cute Rabbit Name Atelier',
    description: 'Browse 140+ Cute rabbit names with tag filters and jump into custom name card generation.',
    status: 'live',
    cta_label: 'Try',
    cta_url: 'tools/rabbit-name-studio.html'
  }
];

const DEFAULT_EMPHASIS_TITLES = new Set(
  DEFAULT_LAB_TOOLS.filter((tool) => tool.emphasis).map((tool) => tool.title)
);

async function loadFallbackHighlights() {
  try {
    const response = await fetch(resolvePublicPath('/data/highlights.json'));
    if (!response.ok) throw new Error(`Failed to load highlights.json (${response.status})`);
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.warn('Unable to load local encyclopedia highlights.', error.message);
    return [];
  }
}

function setPlaceholder(target, text) {
  if (!target) return;
  const tag = target.tagName && target.tagName.toLowerCase() === 'ul' ? 'li' : 'p';
  target.innerHTML = `<${tag} class="placeholder">${text}</${tag}>`;
}

function buildList(items) {
  if (!items || !items.length) return null;
  const ul = document.createElement('ul');
  items.forEach((item) => {
    const li = document.createElement('li');
    li.textContent = item;
    ul.appendChild(li);
  });
  return ul;
}

async function fetchFromTable(tableKey, options = {}) {
  if (!supabase) return [];
  const tableName = SUPABASE_TABLES[tableKey];
  if (!tableName) return [];

  let query = supabase.from(tableName).select('*');

  if (options.order) {
    query = query.order(options.order.column, {
      ascending: options.order.ascending ?? true,
      nullsFirst: options.order.nullsFirst ?? false
    });
  }

  if (options.limit) {
    query = query.limit(options.limit);
  }

  const { data, error } = await query;
  if (error) {
    console.error(`Failed to load ${tableKey}:`, error.message);
    return [];
  }
  return data || [];
}

function renderExhibits(records) {
  const target = targets.exhibits;
  if (!target) return;
  if (!records.length) {
    setPlaceholder(target, 'No exhibits have been published yet.');
    return;
  }

  target.innerHTML = '';
  const fragment = document.createDocumentFragment();

  records.forEach((record) => {
    const article = document.createElement('article');
    article.className = 'exhibit-card';

    const tag = document.createElement('p');
    tag.className = 'tag';
    tag.textContent = record.category || 'Exhibit';

    const title = document.createElement('h3');
    title.textContent = record.title || 'Untitled Exhibit';

    const summary = document.createElement('p');
    summary.textContent = record.summary || 'Description forthcoming.';

    const link = document.createElement('a');
    link.className = 'text-link';
    link.href = record.cta_url || '#';
    link.textContent = record.cta_label || 'View Exhibit →';

    article.append(tag, title, summary, link);
    fragment.appendChild(article);
  });

  target.appendChild(fragment);
}

function renderEncyclopedia(records, { fallback = false } = {}) {
  const target = targets.encyclopedia;
  if (!target) return;
  if (!records.length) {
    setPlaceholder(target, 'Featured articles will appear here soon.');
    return;
  }

  target.innerHTML = '';
  const fragment = document.createDocumentFragment();

  records.forEach((record) => {
    const card = document.createElement('article');
    card.className = 'info-card';

    const title = document.createElement('h3');
    title.textContent = record.title || 'Section';

    const summary = document.createElement('p');
    summary.textContent = record.summary || 'Summary pending.';

    const highlights = Array.isArray(record.highlights)
      ? record.highlights
      : typeof record.highlights === 'string' && record.highlights.length
      ? record.highlights.split(',').map((item) => item.trim())
      : [];

    card.append(title, summary);
    const list = buildList(highlights);
    if (list) card.appendChild(list);
    if (record.url || record.cta_url) {
      const link = document.createElement('a');
      link.className = 'text-link';
      link.href = record.url || record.cta_url;
      link.textContent = record.cta_label || 'Read article →';
      card.appendChild(link);
    }
    fragment.appendChild(card);
  });

  target.appendChild(fragment);

  if (fallback) {
    const note = document.createElement('p');
    note.className = 'placeholder';
    note.textContent = 'Showing curated highlights from the Croissant archive.';
    target.appendChild(note);
  }
}

function renderCollections(records) {
  const target = targets.collections;
  if (!target) return;
  if (!records.length) {
    setPlaceholder(target, 'Upload assets to the atelier collection table.');
    return;
  }

  target.innerHTML = '';
  const fragment = document.createDocumentFragment();

  records.forEach((record) => {
    const card = document.createElement('article');
    card.className = 'atelier-card';

    const title = document.createElement('h3');
    title.textContent = record.title || 'Collection';

    const description = document.createElement('p');
    description.textContent = record.description || 'Details forthcoming.';

    const meta = document.createElement('span');
    const format = record.format || 'ZIP';
    const count = record.asset_count ? `${record.asset_count} assets` : 'Assets coming soon';
    meta.textContent = `${format} • ${count}`;

    const button = document.createElement('button');
    button.className = 'btn';
    button.textContent = record.cta_label || 'Download Pack';
    if (record.download_url) {
      button.addEventListener('click', () => window.open(record.download_url, '_blank'));
    } else {
      button.disabled = true;
    }

    card.append(title, description, meta, button);
    fragment.appendChild(card);
  });

  target.appendChild(fragment);
}

function renderTools(records, { fallback = false } = {}) {
  const target = targets.tools;
  if (!target) return;
  if (!records.length) {
    setPlaceholder(target, 'Lab tools will appear here soon.');
    return;
  }

  target.innerHTML = '';
  const fragment = document.createDocumentFragment();

  records.forEach((record) => {
    const article = document.createElement('article');
    article.className = 'tool-card';

    const rawStatus = (record.status || 'live').toLowerCase();
    const normalizedStatus = rawStatus.replace(/[_-]+/g, ' ');
    const statusTokens = normalizedStatus.split(/[\s,|]+/).filter(Boolean);
    const statusKey = statusTokens.find((token) => statusCopy[token]) || statusTokens[0] || 'live';
    const emphasisedViaStatus = statusTokens.some((token) =>
      ['spotlight', 'emphasis', 'highlight', 'featured'].includes(token)
    );

    const explicitEmphasis =
      record.emphasis ?? record.is_emphasis ?? record.highlighted ?? record.featured;
    const emphasised =
      explicitEmphasis !== undefined
        ? Boolean(explicitEmphasis)
        : emphasisedViaStatus || (record.title ? DEFAULT_EMPHASIS_TITLES.has(record.title) : false);
    if (emphasised) {
      article.classList.add('tool-card--emphasis');
    }

    const status = statusCopy[statusKey] || statusCopy.live;
    const statusTag = document.createElement('p');
    statusTag.className = status.className;
    statusTag.textContent = status.label;

    const title = document.createElement('h3');
    title.className = 'tool-card__title';
    title.textContent = record.title || 'Tool';

    const description = document.createElement('p');
    description.className = 'tool-card__description';
    description.textContent = record.description || 'Description forthcoming.';

    const isLive = statusKey === 'live';
    const actionNode = document.createElement(record.cta_url && isLive ? 'a' : 'button');
    actionNode.textContent = record.cta_label || 'Try';
    actionNode.className = 'btn';
    if (record.cta_url && isLive) {
      actionNode.href = record.cta_url;
      actionNode.target = '_blank';
      actionNode.rel = 'noopener';
    } else {
      actionNode.disabled = true;
    }

    article.append(statusTag, title, description, actionNode);
    fragment.appendChild(article);
  });

  target.appendChild(fragment);

  if (fallback) {
    const note = document.createElement('p');
    note.className = 'placeholder';
    note.textContent = 'Preview tools are available while new experiments are developed.';
    target.appendChild(note);
  }
}

function renderTimeline(records, { noteMessage } = {}) {
  const target = targets.timeline;
  if (!target) return;
  if (!records.length) {
    setPlaceholder(target, 'Add timeline entries to the database.');
    return;
  }

  target.innerHTML = '';
  const fragment = document.createDocumentFragment();

  records.forEach((record) => {
    const wrapper = document.createElement('div');

    const title = document.createElement('h4');
    title.textContent = record.year_label || 'Milestone';

    const description = document.createElement('p');
    description.textContent = record.description || '';

    wrapper.append(title, description);
    fragment.appendChild(wrapper);
  });

  target.appendChild(fragment);

  if (noteMessage) {
    const note = document.createElement('p');
    note.className = 'placeholder';
    note.textContent = noteMessage;
    target.appendChild(note);
  }
}

function renderNewsletter(records) {
  const target = targets.newsletter;
  if (!target) return;

  const displayableRecords = records.filter((record) => !record.email);

  if (!displayableRecords.length) {
    setPlaceholder(target, 'No newsletters archived yet.');
    return;
  }

  target.innerHTML = '';
  displayableRecords.forEach((record) => {
    const li = document.createElement('li');
    const date = record.published_at
      ? new Date(record.published_at).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric'
        })
      : '';
    const title = record.title || 'Newsletter';
    li.textContent = date ? `${date} — ${title}` : title;
    target.appendChild(li);
  });
}

function renderTags(container, tags = []) {
  if (!container || !tags.length) return;
  const wrapper = document.createElement('div');
  wrapper.className = 'tag-chips';
  tags.forEach((tag) => {
    const chip = document.createElement('span');
    chip.className = 'tag-chip';
    chip.textContent = tag;
    wrapper.appendChild(chip);
  });
  container.appendChild(wrapper);
}

function buildImageUrl(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  const normalized = url.startsWith('/') ? url : `/${url}`;
  return resolvePublicPath(normalized);
}

function renderFeaturedArticles(records, { assets = [], tagsBySlug = new Map(), fallback = false } = {}) {
  const target = targets.featured;
  if (!target) return;
  if (!records.length) {
    setPlaceholder(target, 'Featured articles will appear here soon.');
    return;
  }

  const assetBySlug = new Map();
  assets.forEach((asset) => {
    if (asset.slug && !assetBySlug.has(asset.slug)) {
      assetBySlug.set(asset.slug, asset);
    }
  });

  target.innerHTML = '';
  const fragment = document.createDocumentFragment();

  records.forEach((record) => {
    const article = document.createElement('article');
    article.className = 'info-card';

    const imageUrl = buildImageUrl(assetBySlug.get(record.slug)?.image_url || record.image_url);
    if (imageUrl) {
      const img = document.createElement('img');
      img.className = 'info-card__image';
      img.src = imageUrl;
      img.alt = record.title || 'Article image';
      img.loading = 'lazy';
      article.appendChild(img);
    }

    const title = document.createElement('h3');
    const link = document.createElement('a');
    const href = record.cta_url || (record.slug ? `posts/${record.slug}.html` : '#');
    link.href = href;
    link.textContent = record.title || 'Article';
    title.appendChild(link);

    const summary = document.createElement('p');
    summary.textContent = record.summary || 'Story coming soon.';

    const tags = tagsBySlug.get(record.slug) || [];
    const tagMount = document.createElement('div');

    const cta = document.createElement('a');
    cta.className = 'text-link';
    cta.href = href;
    cta.textContent = record.cta_label || 'Read more →';

    article.append(title, summary, tagMount, cta);
    renderTags(tagMount, tags);
    fragment.appendChild(article);
  });

  target.appendChild(fragment);

  if (fallback) {
    const note = document.createElement('p');
    note.className = 'placeholder';
    note.textContent = 'Showing featured articles in preview mode.';
    target.appendChild(note);
  }
}

async function showConfigWarning() {
  const consoleMessage =
    'Supabase not configured. Duplicate supabase/config.example.js as supabase/config.js and add your project URL + anon key.';
  const displayMessage = 'Content is being curated and will appear soon.';
  console.warn(consoleMessage);
  Object.entries(targets).forEach(([key, target]) => {
    if (!target) return;
    if (key === 'timeline' || key === 'encyclopedia') return;
    setPlaceholder(target, displayMessage);
  });
  renderTimeline(DEFAULT_TIMELINE, {
    noteMessage: 'Timeline will update as new milestones are cataloged.'
  });

  const fallbackHighlights = await loadFallbackHighlights();
  if (fallbackHighlights.length) {
    renderEncyclopedia(fallbackHighlights, { fallback: true });
  } else if (targets.encyclopedia) {
    setPlaceholder(targets.encyclopedia, displayMessage);
  }

  renderTools(DEFAULT_LAB_TOOLS, { fallback: true });
  renderFeaturedArticles(DEFAULT_FEATURED_ARTICLES, { fallback: true });
}

async function init() {
  bindSubscribeHandlers();

  if (!hasValidConfig) {
    await showConfigWarning();
    return;
  }

  const [
    exhibits,
    encyclopedia,
    collections,
    tools,
    timeline,
    newsletters,
    articles,
    articleAssets,
    articleTags
  ] = await Promise.all([
    fetchFromTable('exhibits', { order: { column: 'position', ascending: true } }),
    fetchFromTable('encyclopedia', { order: { column: 'position', ascending: true } }),
    fetchFromTable('collections', { order: { column: 'position', ascending: true } }),
    fetchFromTable('tools', { order: { column: 'position', ascending: true } }),
    fetchFromTable('timeline', { order: { column: 'year_index', ascending: true } }),
    fetchFromTable('newsletter', { order: { column: 'published_at', ascending: false }, limit: 5 }),
    fetchFromTable('articles', { order: { column: 'created_at', ascending: false } }),
    fetchFromTable('article_assets', { order: { column: 'created_at', ascending: false } }),
    fetchFromTable('article_tags', { order: { column: 'created_at', ascending: false } })
  ]);

  const encyclopediaRecords = encyclopedia.length ? encyclopedia : await loadFallbackHighlights();
  const labTools = tools.length ? tools : DEFAULT_LAB_TOOLS;
  const featuredArticles = articles.length ? articles : DEFAULT_FEATURED_ARTICLES;
  const featuredAssets = articles.length ? articleAssets : [];
  const tagsBySlug = new Map();
  (articleTags || []).forEach((item) => {
    if (!item.slug || !item.tag) return;
    if (!tagsBySlug.has(item.slug)) tagsBySlug.set(item.slug, []);
    tagsBySlug.get(item.slug).push(item.tag);
  });

  renderExhibits(exhibits);
  renderEncyclopedia(encyclopediaRecords, { fallback: !encyclopedia.length });
  renderCollections(collections);
  renderTools(labTools, { fallback: !tools.length });
  const timelineFallback = !timeline.length;
  const timelineRecords = timelineFallback ? DEFAULT_TIMELINE : timeline;
  renderTimeline(timelineRecords, {
    noteMessage: timelineFallback ? 'Timeline will update as new milestones are cataloged.' : undefined
  });
  renderNewsletter(newsletters);
  renderFeaturedArticles(featuredArticles, {
    assets: featuredAssets,
    tagsBySlug,
    fallback: !articles.length
  });
}

async function bootstrap() {
  try {
    const configModule = await loadSupabaseConfig();
    applySupabaseConfig(configModule);
  } catch (error) {
    console.warn('Unable to load Supabase config, running in preview mode.', error);
    applySupabaseConfig();
  }
  await init();
}

bootstrap();
